import { useEffect, useMemo, useState } from "react";
import { Chessboard } from "react-chessboard";
import type { ChessboardOptions } from "react-chessboard";
import { applySanMove, applyUciMove } from "@onchainreps/chess";
import type { StoredRepertoire } from "@onchainreps/storage";

const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

interface RepertoireTrainerProps {
  lines: StoredRepertoire[];
  onExit: () => void;
}

type TrainerStatus = "yourMove" | "correct" | "wrong" | "done";

function isPlayerPly(index: number, color: "white" | "black"): boolean {
  return color === "white" ? index % 2 === 0 : index % 2 === 1;
}

export function RepertoireTrainer({ lines, onExit }: RepertoireTrainerProps) {
  const [lineIndex, setLineIndex] = useState(0);
  const [index, setIndex] = useState(0);
  const [fen, setFen] = useState(START_FEN);
  const [status, setStatus] = useState<TrainerStatus>("yourMove");
  const [wrongMove, setWrongMove] = useState<string | null>(null);
  const [played, setPlayed] = useState<string[]>([]);

  const line = lines[lineIndex] ?? lines[0];
  const playerColor = line?.color ?? "white";
  const moves = line?.moves ?? [];

  const reset = () => {
    setIndex(0);
    setFen(START_FEN);
    setPlayed([]);
    setStatus("yourMove");
    setWrongMove(null);
  };

  const nextLine = () => {
    setLineIndex((current) => (current + 1) % Math.max(lines.length, 1));
    reset();
  };

  const expectedSan = index < moves.length ? moves[index] : null;

  useEffect(() => {
    if (!line || index >= moves.length) return;
    if (isPlayerPly(index, playerColor)) return;
    const san = moves[index];
    if (!san) return;
    const timer = window.setTimeout(() => {
      const applied = applySanMove(fen, san);
      if (!applied.legal) return;
      setFen(applied.fen);
      setPlayed((current) => [...current, san]);
      setIndex((current) => current + 1);
    }, 420);
    return () => window.clearTimeout(timer);
  }, [fen, index, line, moves, playerColor]);

  const options: ChessboardOptions = useMemo(
    () => ({
      position: fen,
      boardOrientation: playerColor === "white" ? "white" : "black",
      allowDragging: status === "yourMove" && Boolean(expectedSan),
      showNotation: true,
      animationDurationInMs: 160,
      darkSquareStyle: { backgroundColor: "var(--board-dark)" },
      lightSquareStyle: { backgroundColor: "var(--board-light)" },
      boardStyle: { borderRadius: "6px", boxShadow: "none" },
      squareStyles:
        status === "wrong" && wrongMove
          ? {
              [wrongMove.slice(0, 2)]: { backgroundColor: "rgba(180, 35, 42, 0.35)" },
              [wrongMove.slice(2, 4)]: { backgroundColor: "rgba(180, 35, 42, 0.45)" },
            }
          : undefined,
      onPieceDrop: ({ sourceSquare, targetSquare }) => {
        if (!sourceSquare || !targetSquare) return false;
        if (status !== "yourMove" || !expectedSan) return false;
        const uci = `${sourceSquare}${targetSquare}`;
        const applied = applyUciMove(fen, uci);
        if (!applied.legal) return false;

        if (applied.san !== expectedSan) {
          setWrongMove(applied.uci);
          setStatus("wrong");
          return true;
        }

        setFen(applied.fen);
        setPlayed((current) => [...current, applied.san]);
        setIndex((current) => current + 1);
        setWrongMove(null);
        setStatus("correct");
        return true;
      },
    }),
    [expectedSan, fen, playerColor, status, wrongMove],
  );

  if (!line) {
    return (
      <div className="panel__body">
        <p className="notice">There is nothing to train yet.</p>
      </div>
    );
  }

  const finished = index >= moves.length;

  return (
    <div className="repertoire">
      <div className="repertoire__head">
        <div>
          <h2 className="progress__title">{line.name}</h2>
          <p className="planned__desc">
            You play {playerColor}. Move {Math.floor(index / 2) + 1} of {Math.ceil(moves.length / 2)}.
          </p>
        </div>
        <button className="btn btn--ghost" onClick={onExit}>
          Back to the builder
        </button>
      </div>

      <div className="repertoire__board">
        <Chessboard options={options} />
      </div>

      <div className="repertoire__moves">
        {played.map((san, moveIndex) => (
          <span className="repertoire__move" key={`${san}-${moveIndex}`}>
            <span className="repertoire__move-label">
              {moveIndex % 2 === 0 ? `${moveIndex / 2 + 1}.` : `${(moveIndex + 1) / 2}...`}
            </span>
            {san}
          </span>
        ))}
      </div>

      <div className="repertoire__actions">
        {status === "wrong" ? (
          <>
            <span className="notice notice--error">That is not the move you saved. Try again.</span>
            <button
              className="btn btn--ghost"
              onClick={() => {
                setStatus("yourMove");
                setWrongMove(null);
              }}
            >
              Retry
            </button>
          </>
        ) : null}
        {status === "correct" ? (
          <>
            <span className="notice">Correct.</span>
            {finished ? (
              <button className="btn btn--primary" onClick={nextLine}>
                Next line
              </button>
            ) : null}
          </>
        ) : null}
        {status === "yourMove" && expectedSan ? <span className="notice">Your move.</span> : null}
        {status === "yourMove" && !expectedSan && finished ? (
          <>
            <span className="notice">Line complete.</span>
            <button className="btn btn--primary" onClick={nextLine}>
              Next line
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
